import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, DocumentTextIcon, BuildingOfficeIcon, MapPinIcon, PhoneIcon } from '@heroicons/react/24/outline'
import { colorMap, formatDate } from '../../Common'

const ModalVisualizacaoCliente = ({ isOpen, onClose, cliente, color = 'green' }) => {
    const colorClasses = colorMap[color] || colorMap.green

    if (!cliente) return null

    let atividadesSecundarias = []
    try {
        atividadesSecundarias = JSON.parse(cliente.atividades_economicas_secundarias || '[]')
    } catch {
        atividadesSecundarias = []
    }

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white px-4 pb-6 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:p-6">
                                <div className="absolute right-0 top-0 pr-4 pt-4">
                                    <button
                                        type="button"
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
                                        onClick={onClose}
                                    >
                                        <span className="sr-only">Fechar</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>

                                <div className="border-b border-gray-200 pb-4">
                                    <div className="inline-flex items-center justify-center w-full px-4 py-2 rounded-lg bg-gray-50 mb-3">
                                        <span className="text-sm font-semibold text-gray-700">
                                            👁️ Visualização
                                        </span>
                                    </div>
                                    <h3 className="text-2xl font-bold leading-7 text-center text-gray-900">
                                        Detalhes do Cliente
                                    </h3>
                                    <p className="mt-2 text-sm text-gray-500 text-center">
                                        Informações completas do cliente cadastrado
                                    </p>
                                </div>

                                <div className="mt-6 space-y-8 max-h-[60vh] overflow-y-auto pr-2">
                                    <div className="border-b border-gray-200 pb-8">
                                        <div className="flex items-center gap-3 mb-4">
                                            <BuildingOfficeIcon className="h-5 w-5 text-green-600" />
                                            <h4 className="text-base font-semibold text-gray-900">Informações da Empresa</h4>
                                        </div>
                                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    CNPJ
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900">
                                                    {cliente.cnpj}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Data de Abertura
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900">
                                                    {formatDate(cliente.data_abertura)}
                                                </p>
                                            </div>

                                            <div className="sm:col-span-2">
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Nome Empresarial
                                                </label>
                                                <p className="mt-1 text-base font-semibold text-gray-900">
                                                    {cliente.nome_empresarial}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Nome Fantasia
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900">
                                                    {cliente.nome_fantasia}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Porte
                                                </label>
                                                <p className="mt-1">
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        {cliente.porte}
                                                    </span>
                                                </p>
                                            </div>

                                            <div className="sm:col-span-2">
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Atividade Econômica Principal
                                                </label>
                                                <p className="mt-1 text-sm text-gray-900">
                                                    {cliente.atividade_economica_principal}
                                                </p>
                                            </div>

                                            {atividadesSecundarias.length > 0 && (
                                                <div className="sm:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-500 mb-2">
                                                        Atividades Econômicas Secundárias
                                                    </label>
                                                    <ul className="space-y-2">
                                                        {atividadesSecundarias.map((atividade, index) => (
                                                            <li key={index} className="flex items-start">
                                                                <span className="inline-flex items-center justify-center w-5 h-5 mr-2 text-xs font-medium text-green-800 bg-green-100 rounded-full flex-shrink-0 mt-0.5">
                                                                    {index + 1}
                                                                </span>
                                                                <span className="text-sm text-gray-900">{atividade}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            <div className="sm:col-span-2">
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Natureza Jurídica
                                                </label>
                                                <p className="mt-1 text-sm text-gray-900">
                                                    {cliente.natureza_juridica}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="border-b border-gray-200 pb-8">
                                        <div className="flex items-center gap-3 mb-4">
                                            <MapPinIcon className="h-5 w-5 text-green-600" />
                                            <h4 className="text-base font-semibold text-gray-900">Endereço</h4>
                                        </div>
                                        <div className="mt-4 space-y-4">
                                            <div className="bg-gray-50 rounded-lg p-4">
                                                <p className="text-sm text-gray-900">
                                                    <span className="font-medium">{cliente.logradouro}, {cliente.numero}</span>
                                                    {cliente.complemento && <span> - {cliente.complemento}</span>}
                                                </p>
                                                <p className="text-sm text-gray-900 mt-1">
                                                    {cliente.bairro} - CEP: {cliente.cep}
                                                </p>
                                                <p className="text-sm text-gray-900 mt-1">
                                                    {cliente.municipio}/{cliente.uf}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="border-b border-gray-200 pb-8">
                                        <div className="flex items-center gap-3 mb-4">
                                            <PhoneIcon className="h-5 w-5 text-green-600" />
                                            <h4 className="text-base font-semibold text-gray-900">Contato e Inscrições</h4>
                                        </div>
                                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    E-mail
                                                </label>
                                                <p className="mt-1 text-sm text-gray-900">
                                                    {cliente.endereco_eletronico}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Telefone
                                                </label>
                                                <p className="mt-1 text-sm text-gray-900">
                                                    {cliente.telefone}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Inscrição Municipal
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900">
                                                    {cliente.inscricao_municipal}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Inscrição Estadual
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900">
                                                    {cliente.inscricao_estadual}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex items-center gap-3 mb-4">
                                            <DocumentTextIcon className="h-5 w-5 text-green-600" />
                                            <h4 className="text-base font-semibold text-gray-900">Situação Cadastral</h4>
                                        </div>
                                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Situação
                                                </label>
                                                <div className="mt-2">
                                                    <span className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-semibold ${cliente.situacao_cadastral === 'Ativa' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                        {cliente.situacao_cadastral}
                                                    </span>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Data da Situação
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900">
                                                    {formatDate(cliente.data_situacao_cadastral)}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Data de Cadastro no Sistema
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900">
                                                    {formatDate(cliente.data_criacao)}
                                                </p>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-500">
                                                    Última Atualização
                                                </label>
                                                <p className="mt-1 text-sm font-semibold text-gray-900">
                                                    {formatDate(cliente.data_atualizacao)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8 flex items-center justify-end border-t border-gray-200 pt-6">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className={`rounded-lg px-6 py-2.5 text-sm font-semibold text-white shadow-sm ${colorClasses.headerButtonBg} ${colorClasses.headerButtonHover} transition-all`}
                                    >
                                        Fechar
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

export default ModalVisualizacaoCliente

